<template>
  <v-container>
    <address-verification-modal
      v-model="modalOpen"
      :original-address="validationPayload"
      :suggested-address="suggestedAddress"
      @close-modal="modalOpen = false"
      @handle-address-selection="handleAddressSelection"
    />
    <property-details-form
      :edit-mode="true"
      :property-id="propertyId"
      :is-loading="isLoading"
      v-bind.sync="editPropertyDetails"
      @cancel-edit-property="onCancelEdit"
      @delete-property="onDeleteProperty"
      @save-property-details="validateAddress"
    />
  </v-container>
</template>

<script>
  import PropertyDetailsForm from '@/components/properties/PropertyDetailsForm'
  import propertyDetailsRouterMixin from '@/helpers/property-details-router-mixin'
  import validationAxios from '@/helpers/validation-client'
  import AddressVerificationModal from '@/components/properties/AddressVerificationModal'
  import { mapActions, mapGetters } from 'vuex'
  import isEqual from 'lodash.isequal'

  export default {
    name: 'EditPropertyDetails',
    components: { PropertyDetailsForm, AddressVerificationModal },
    mixins: [propertyDetailsRouterMixin],
    data () {
      return {
        modalOpen: false,
        isLoading: false,
        addressValid: null,
        suggestedAddress: {},
        editPropertyDetails: {
          name: '',
          propertyType: '',
          area: null,
          maxOccupancy: null,
          street1: '',
          street2: '',
          city: '',
          region: '',
          postalcode: '',
          country: '',
          tags: [],
        },
      }
    },
    computed: {
      ...mapGetters({
        savedPropertyDetails: 'Properties/propertyDetails',
      }),
      propertyId () {
        return this.$route.params.id
      },
      propertyName () {
        return this.savedPropertyDetails.name
      },
      validationPayload () {
        return this.getAddress(this.editPropertyDetails)
      },
    },
    created () {
      if (!this.savedPropertyDetails || !Object.keys(this.savedPropertyDetails).length) {
        this.fetchPropertyDetails(this.propertyId).then(() => {
          this.initializePropertyDetailsForm()
        })
      } else {
        this.initializePropertyDetailsForm()
      }
    },
    methods: {
      ...mapActions({
        fetchPropertyDetails: 'Properties/fetchPropertyDetails',
        updatePropertyDetails: 'Properties/updatePropertyDetails',
        deleteProperty: 'Properties/deleteProperty',
      }),
      getAddress (propertyDetails) {
        return {
          street1: propertyDetails?.street1 ?? '',
          street2: propertyDetails?.street2 ?? '',
          city: propertyDetails?.city ?? '',
          region: propertyDetails?.region ?? '',
          postalcode: propertyDetails?.postalcode ?? '',
          country: propertyDetails?.country ?? '',
        }
      },
      onCancelEdit () {
        this.initializePropertyDetailsForm()
        this.$router.replace(`/properties/${this.propertyId}`)
      },
      validateAddress () {
        const beforeChangedAddress = this.getAddress(this.savedPropertyDetails)
        if (!isEqual(this.validationPayload, beforeChangedAddress)) {
          validationAxios.post(`${this.$store.state.validatorURL}/api/v1/validation/address`, this.validationPayload)
            .then(res => {
              this.suggestedAddress = this.getAddress(res.data.address)
              if (!isEqual(this.validationPayload, this.suggestedAddress)) this.modalOpen = true
              else this.savePropertyDetailsForm()
            })
        } else this.savePropertyDetailsForm()
      },
      handleAddressSelection (address) {
        for (const item in address) {
          this.editPropertyDetails[item] = address[item]
        }
        this.savePropertyDetailsForm()
        this.modalOpen = false
      },
      initializePropertyDetailsForm () {
        this.editPropertyDetails = {
          name: this.savedPropertyDetails.name || '',
          propertyType: this.savedPropertyDetails.propertyType || '',
          area: this.savedPropertyDetails.area || null,
          maxOccupancy: this.savedPropertyDetails.maxOccupancy || null,
          street1: this.savedPropertyDetails.street1 || '',
          street2: this.savedPropertyDetails.street2 || '',
          city: this.savedPropertyDetails.city || '',
          region: this.savedPropertyDetails.region || '',
          postalcode: this.savedPropertyDetails.postalcode || '',
          country: this.savedPropertyDetails.country || '',
          tags: this.savedPropertyDetails.tags || [],
        }
      },
      onDeleteProperty () {
        this.$root.$confirm({
          icon: '$faTrash',
          minHeight: 510,
          width: 400,
          title: 'Delete Property',
          text: `Confirm you want to delete property <span style="font-weight: bold">${this.propertyName}</span>. Please note that this will have no impact on your billing. To make billing adjustments, please contact support.`,
          confirmText: 'Yes, delete',
          cancelText: 'cancel',
        })
          .then(() => { this.onConfirmDelete() })
      },
      onConfirmDelete () {
        this.deleteProperty(this.propertyId)
          .then(() => this.$router.replace('/properties'))
      },
      savePropertyDetailsForm () {
        const payload = {}
        const { tags, ...compareObj } = this.editPropertyDetails
        const payloadKeys = Object.keys(compareObj)
        // check to see if there was an actual change in the data by comparing saved vs edited data
        for (const key of payloadKeys) {
          if (compareObj[key] !== this.savedPropertyDetails[key]) {
            payload[key] = this.editPropertyDetails[key]
          }
        }
        // compare the prev and curr tags arrays
        if (JSON.stringify(tags) !== JSON.stringify(this.savedPropertyDetails.tags)) {
          payload.tagData = { tags: tags.map(tag => ({ tag })) }
        }
        // due to backend weirdness and, generally, the weirdness of a user setting a field to null, below is to handle max occ/sqft being removed without causing both to be removed
        if (Object.keys(payload).indexOf('maxOccupancy') === -1) {
          payload.maxOccupancy =
            this.savedPropertyDetails.maxOccupancy || null
        }
        if (Object.keys(payload).indexOf('area') === -1) payload.area = this.savedPropertyDetails.area || null
        // check for a payload length before making api call
        if (Object.keys(payload).length) {
          this.isLoading = true
          this.updatePropertyDetails({ propertyId: this.propertyId, payload })
            .finally(() => { this.isLoading = false })
        } else {
          const toastPayload = { type: 'error', text: "You didn't change anything!", unread: true }
          this.$store.dispatch('updateMessages', toastPayload)
        }
      },
    },
  }
</script>
